import React from 'react';

import { safeLogQueryError } from '@helpers/query/Client';
import { AppQueryKey } from '@helpers/query/Keys';
import {
  MutateOptions,
  QueryFunction,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
  useMutation as useTanStackMutation,
  useQuery as useTanStackQuery,
  useQueryClient as useTanStackQueryClient,
} from '@tanstack/react-query';

/** App-owned query options; feature code should use this instead of TanStack options directly. */
export type AppQueryOptions<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends AppQueryKey = AppQueryKey,
> = Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'> & {
  queryKey: TQueryKey;
  queryFn: QueryFunction<TQueryFnData, TQueryKey>;
};

/** App-owned query result type returned by useAppQuery. */
export type AppQueryResult<TData, TError = Error> = UseQueryResult<TData, TError>;

/** App-owned mutation options; feature code should use this instead of TanStack options directly. */
export type AppMutationOptions<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
> = UseMutationOptions<TData, TError, TVariables, AppMutationContext<TContext>>;

/** App-owned mutation result type returned by useAppMutation. */
export type AppMutationResult<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
> = Omit<
  UseMutationResult<TData, TError, TVariables, AppMutationContext<TContext>>,
  'mutateAsync'
> & {
  /** Runs a mutation and returns an outcome instead of rejecting on failure. */
  mutateAsync: (
    ...args: Parameters<
      UseMutationResult<TData, TError, TVariables, AppMutationContext<TContext>>['mutateAsync']
    >
  ) => Promise<AppMutationOutcome<TData, TError>>;
  /** Escape hatch for code that intentionally wants TanStack Query's rejecting promise API. */
  mutateAsyncOrThrow: UseMutationResult<
    TData,
    TError,
    TVariables,
    AppMutationContext<TContext>
  >['mutateAsync'];
};

type AppMutationContext<TContext> = TContext | undefined;

export type AppMutationOutcome<TData = unknown, TError = Error> =
  | { data: TData; ok: true }
  | { error: TError; ok: false };

/** Minimal cache API exposed to feature code and subscription adapters. */
export type AppQueryCache = {
  cancelQueries: (queryKey: AppQueryKey) => Promise<void>;
  getQueryData: <TData>(queryKey: AppQueryKey) => TData | undefined;
  invalidateQueries: (queryKey: AppQueryKey) => Promise<void>;
  removeQueries: (queryKey: AppQueryKey) => void;
  setQueryData: <TData>(
    queryKey: AppQueryKey,
    updater: TData | undefined | ((currentData: TData | undefined) => TData | undefined)
  ) => void;
};

export function useAppQuery<
  TQueryFnData,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends AppQueryKey = AppQueryKey,
>(options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey>): AppQueryResult<TData, TError> {
  return useTanStackQuery(options);
}

export function useAppMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: AppMutationOptions<TData, TError, TVariables, TContext>
): AppMutationResult<TData, TError, TVariables, TContext> {
  const safeOptions = useSafeMutationOptions(options);
  const mutation = useTanStackMutation(safeOptions);
  const mutate = React.useCallback<
    UseMutationResult<TData, TError, TVariables, AppMutationContext<TContext>>['mutate']
  >(
    (variables, mutateOptions) => {
      mutation.mutate(variables, getSafeMutateOptions(mutateOptions, options.mutationKey));
    },
    [mutation, options.mutationKey]
  );
  const mutateAsync = React.useCallback<
    AppMutationResult<TData, TError, TVariables, TContext>['mutateAsync']
  >(
    async (variables, mutateOptions) => {
      try {
        return {
          data: await mutation.mutateAsync(
            variables,
            getSafeMutateOptions(mutateOptions, options.mutationKey)
          ),
          ok: true,
        };
      } catch (error) {
        return { error: error as TError, ok: false };
      }
    },
    [mutation, options.mutationKey]
  );
  const mutateAsyncOrThrow = React.useCallback<
    UseMutationResult<TData, TError, TVariables, AppMutationContext<TContext>>['mutateAsync']
  >(
    (variables, mutateOptions) =>
      mutation.mutateAsync(variables, getSafeMutateOptions(mutateOptions, options.mutationKey)),
    [mutation, options.mutationKey]
  );

  return {
    ...mutation,
    mutate,
    mutateAsync,
    mutateAsyncOrThrow,
  };
}

function useSafeMutationOptions<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: AppMutationOptions<TData, TError, TVariables, TContext>
): AppMutationOptions<TData, TError, TVariables, TContext> {
  const onSuccess = options.onSuccess;
  const onError = options.onError;
  const onSettled = options.onSettled;
  const onMutate = options.onMutate;

  return React.useMemo(
    () => ({
      ...options,
      onMutate: onMutate
        ? async (
            ...args: Parameters<
              NonNullable<AppMutationOptions<TData, TError, TVariables, TContext>['onMutate']>
            >
          ) => {
            return await callMutationCallbackSafely(
              onMutate,
              args,
              'onMutate',
              options.mutationKey
            );
          }
        : undefined,
      onSuccess: onSuccess
        ? async (
            ...args: Parameters<
              NonNullable<AppMutationOptions<TData, TError, TVariables, TContext>['onSuccess']>
            >
          ) => {
            await callMutationCallbackSafely(onSuccess, args, 'onSuccess', options.mutationKey);
          }
        : undefined,
      onError: onError
        ? async (
            ...args: Parameters<
              NonNullable<AppMutationOptions<TData, TError, TVariables, TContext>['onError']>
            >
          ) => {
            await callMutationCallbackSafely(onError, args, 'onError', options.mutationKey);
          }
        : undefined,
      onSettled: onSettled
        ? async (
            ...args: Parameters<
              NonNullable<AppMutationOptions<TData, TError, TVariables, TContext>['onSettled']>
            >
          ) => {
            await callMutationCallbackSafely(onSettled, args, 'onSettled', options.mutationKey);
          }
        : undefined,
    }),
    [onError, onMutate, onSettled, onSuccess, options]
  );
}

function getSafeMutateOptions<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  mutateOptions: MutateOptions<TData, TError, TVariables, AppMutationContext<TContext>> | undefined,
  mutationKey: AppMutationOptions<TData, TError, TVariables, TContext>['mutationKey']
): MutateOptions<TData, TError, TVariables, AppMutationContext<TContext>> | undefined {
  if (!mutateOptions) return undefined;

  const onError = mutateOptions.onError;
  const onSettled = mutateOptions.onSettled;
  const onSuccess = mutateOptions.onSuccess;

  return {
    ...mutateOptions,
    onError: onError
      ? (
          ...args: Parameters<
            NonNullable<
              MutateOptions<TData, TError, TVariables, AppMutationContext<TContext>>['onError']
            >
          >
        ) => {
          void callMutationCallbackSafely(onError, args, 'onError', mutationKey);
        }
      : undefined,
    onSettled: onSettled
      ? (
          ...args: Parameters<
            NonNullable<
              MutateOptions<TData, TError, TVariables, AppMutationContext<TContext>>['onSettled']
            >
          >
        ) => {
          void callMutationCallbackSafely(onSettled, args, 'onSettled', mutationKey);
        }
      : undefined,
    onSuccess: onSuccess
      ? (
          ...args: Parameters<
            NonNullable<
              MutateOptions<TData, TError, TVariables, AppMutationContext<TContext>>['onSuccess']
            >
          >
        ) => {
          void callMutationCallbackSafely(onSuccess, args, 'onSuccess', mutationKey);
        }
      : undefined,
  };
}

async function callMutationCallbackSafely<TArgs extends readonly unknown[], TResult>(
  callback: (...args: TArgs) => TResult,
  args: TArgs,
  callbackName: string,
  mutationKey: unknown
): Promise<Awaited<TResult> | undefined> {
  try {
    return await callback(...args);
  } catch (error) {
    safeLogQueryError(error, {
      mutationCallback: callbackName,
      mutationKey,
    });
    return undefined;
  }
}

export function useAppQueryCache(): AppQueryCache {
  const queryClient = useTanStackQueryClient();

  return React.useMemo(
    () => ({
      cancelQueries: (queryKey: AppQueryKey) => queryClient.cancelQueries({ queryKey }),
      getQueryData: <TData>(queryKey: AppQueryKey) => queryClient.getQueryData<TData>(queryKey),
      invalidateQueries: (queryKey: AppQueryKey) => queryClient.invalidateQueries({ queryKey }),
      removeQueries: (queryKey: AppQueryKey) => queryClient.removeQueries({ queryKey }),
      setQueryData: <TData>(
        queryKey: AppQueryKey,
        updater: TData | undefined | ((currentData: TData | undefined) => TData | undefined)
      ) => {
        queryClient.setQueryData<TData>(queryKey, updater);
      },
    }),
    [queryClient]
  );
}
